import { useMsal } from '@azure/msal-react';
import { AccordionHeader, AccordionItem, AccordionPanel, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useStores } from '../../../common/hooks';
import { signOut } from '../../../common/msal';
import { isPpeEnvironment } from '../../../configurations';
import { RoutePaths } from '../../../route-paths';
import { SwitchProfilesModal } from '../SwitchProfilesModal';
import './UserProfileMenu.scss';
import { useMenuStyles } from './use-menu-styles';

interface Props {
  renderInAppDrawer?: {
    onMenuItemClick: () => void;
  };
}

const UserProfileMenu: React.FC<Props> = ({ renderInAppDrawer }) => {
  const {
    instance
  } = useMsal();

  const activeAccount = instance.getActiveAccount();
  const [isSwitchingProfilesModalOpen, setIsSwitchingProfilesModalOpen] = useState(false);
  const [isOpenMenuPopover, setIsOpenMenuPopover] = useState(false);
  const classes = useMenuStyles();

  const {
    userStore,
    idleStore,
  } = useStores();

  const title = useMemo(() => `Welcome, ${userStore.displayName}`, [userStore.displayName]);

  const renderMenuItemLink = (displayName: string, to: string, onClick?: () => void) => (
    <Link
      role='menuitem'
      to={to}
      className={classes.menuItem}
      onClick={() => {
        if (onClick) onClick();
        if (renderInAppDrawer) {
          renderInAppDrawer.onMenuItemClick();
          return;
        }
        setIsOpenMenuPopover(false);
      }}
    >
      {displayName}
    </Link>
  );

  const renderMenuItem = (displayName: string, onClick: () => void) => {
    if (renderInAppDrawer) {
      return (
        <button
          onClick={() => {
            onClick();
            renderInAppDrawer.onMenuItemClick();
          }}
        >
          {displayName}
        </button>
      )
    }

    return (
      <MenuItem
        className={classes.menuItem}
        onClick={onClick}
      >
        {displayName}
      </MenuItem>
    )
  };

  const renderMenuList = () => (
    <>
      {!userStore.isStaff && renderMenuItemLink('My Profile', RoutePaths.UserProfile)}
      {
        isPpeEnvironment
        && userStore.isAdmin
        && !userStore.isShowPartnerPage
        && renderMenuItem('Switch Profiles', () => setIsSwitchingProfilesModalOpen(true))
      }
      {
        (userStore.isAdmin || userStore.isLead)
        && userStore.isShowPartnerPage
        && renderMenuItemLink('Return to admin dashboard', RoutePaths.ProgramDashboard, () => userStore.setIsShowPartnerPage(false))}
      {
        (!(userStore.isAdmin || userStore.isLead) && userStore.isJudge)
        && userStore.isShowPartnerPage
        && renderMenuItemLink('Return to judging dashboard', RoutePaths.JudgingDashboard, () => userStore.setIsShowPartnerPage(false))
      }
      {
        !userStore.userInfo?.email.toLowerCase().includes('@microsoft.com')
        && !userStore.isShowPartnerPage
        && renderMenuItemLink('Return to partner dashboard', RoutePaths.Home, () => userStore.setIsShowPartnerPage(true))
      }
      {
        renderMenuItem('Logout', () => {
          if (activeAccount) {
            userStore.removeLastUpdateUserInfo(activeAccount.username);
            idleStore.removeActivity();
          }
          signOut(instance);
        })
      }
    </>
  );

  if (renderInAppDrawer) return (
    <AccordionItem
      value='user-profile-menu'
    >
      <AccordionHeader expandIconPosition='end'>{title}</AccordionHeader>
      <AccordionPanel>
        {renderMenuList()}
      </AccordionPanel>
    </AccordionItem>
  );

  return (
    <div
      className='user-profile-menu'
    >
      <Menu
        open={isOpenMenuPopover}
        onOpenChange={() => setIsOpenMenuPopover(!isOpenMenuPopover)}
        positioning={{
          autoSize: true,
          align: 'end',
          offset: { mainAxis: 28, crossAxis: 35 },
        }}
      >
        <MenuTrigger>
          <button
            onClick={() => setIsOpenMenuPopover(!isOpenMenuPopover)}
          >
            {title}
          </button>
        </MenuTrigger>

        <MenuPopover>
          <MenuList>
            {renderMenuList()}
          </MenuList>
        </MenuPopover>
      </Menu>
      {
        isSwitchingProfilesModalOpen &&
        <SwitchProfilesModal
          isOpen={isSwitchingProfilesModalOpen}
          onDismiss={() => setIsSwitchingProfilesModalOpen(false)}
        />
      }
    </div >
  );
};

export default observer(UserProfileMenu);
